import { ReactNode, useMemo } from 'react'

import { Col, Divider, Row, Space } from 'antd'
import { createStyles } from 'antd-style'

import { Fullscreen } from './Fullscreen'
import { ToolbarProps } from './interface'
import { Refresh } from './Refresh'
import { SearchInput } from './SearchInput'

const useStyles = createStyles(({ token }) => ({
  toolbar: {
    paddingBlockEnd: token.padding
  }
}))

const EMPTY_ARRAY: ReactNode[] = []
const EMPTY_OBJECT: Partial<Record<'left' | 'center' | 'right', ReactNode[]>> =
  {}

const defaultActions: ToolbarProps['actions'] = ['refresh', 'fullscreen']

const useWidget = (actions = defaultActions) => {
  return useMemo(() => {
    const widgets: ReactNode[] = []
    if (actions.includes('refresh')) {
      widgets.push(<Refresh key='Refresh' />)
    }
    if (actions.includes('fullscreen')) {
      widgets.push(<Fullscreen key='FullScreen' />)
    }
    return widgets
  }, [actions])
}

export const Toolbar = ({
  style,
  actions,
  search,
  extraActions = EMPTY_OBJECT
}: ToolbarProps) => {
  const widgets = useWidget(actions)
  const {
    left: leftActions = EMPTY_ARRAY,
    center: searchLeftActions = EMPTY_ARRAY,
    right: searchRightActions = EMPTY_ARRAY
  } = extraActions

  const searchInputProps = search
    ? Array.isArray(search)
      ? {
          fields: search
        }
      : search
    : undefined

  const searchInput = searchInputProps && (
    <SearchInput key='toolbarSearch' {...searchInputProps} />
  )

  const searchLeftActionsGroup = [...searchLeftActions]

  if (searchInput) {
    searchLeftActionsGroup.push(searchInput)
  }

  const searchRightActionsGroup = [...widgets, ...searchRightActions]

  const rightActions = [
    searchLeftActionsGroup.length > 0 && (
      <Space key='searchLeftGroup' size='large'>
        {searchLeftActionsGroup}
      </Space>
    ),
    searchRightActionsGroup.length > 0 && (
      <Space key='searchRightGroup'>{searchRightActionsGroup}</Space>
    )
  ]

  const { cx, styles } = useStyles()

  return (
    <Row className={cx(styles.toolbar, 'crud-table-toolbar')} style={style}>
      <Col flex='none'>
        <Space size='large'>{leftActions}</Space>
      </Col>
      <Col flex='auto'>
        <Row justify='end'>
          <Space separator={<Divider orientation='vertical' />}>
            {rightActions}
          </Space>
        </Row>
      </Col>
    </Row>
  )
}
